import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { Spin } from "antd";
import { useUser } from "../../contexts/UserContext/UserContext";
import { useToast } from "../../contexts/ToastContext";
import {
    cleanupPaymentUrlParams,
    hasPaymentRelatedParams,
    isPaymentFailed,
    parseCoverageAddedInfo,
    verifyStripePaymentReturn,
} from "../reservations/lib/reservationPayment";
import CoverageAddedContent from "./components/CoverageAddedContent";
import PaymentFailureView from "./components/PaymentFailureView";

const CoverageAddedPage = () => {
    const { id: reservationId } = useParams();
    const location = useLocation();
    const { t } = useTranslation("addCoverage");
    const toast = useToast();
    const { state } = useUser();

    const fullPaymentRequired =
        state?.myCompanyData?.preferences?.fullPaymentRequiredToCreateReservation || false;

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const isPaymentReturn = hasPaymentRelatedParams(searchParams);
    const info = useMemo(() => parseCoverageAddedInfo(searchParams), [searchParams]);

    const [ready, setReady] = useState(!isPaymentReturn);
    const [paymentFailed, setPaymentFailed] = useState(false);

    useEffect(() => {
        if (!reservationId || !isPaymentReturn) {
            setReady(true);
            return undefined;
        }

        let cancelled = false;

        const verify = async () => {
            if (isPaymentFailed(searchParams, fullPaymentRequired)) {
                setPaymentFailed(true);
                setReady(true);
                return;
            }

            try {
                await verifyStripePaymentReturn(reservationId);
                cleanupPaymentUrlParams();
            } catch {
                if (!cancelled) {
                    toast.error({ message: t("paymentVerifyError") });
                    setPaymentFailed(true);
                }
            } finally {
                if (!cancelled) setReady(true);
            }
        };

        verify();
        return () => {
            cancelled = true;
        };
    }, [fullPaymentRequired, isPaymentReturn, toast, reservationId, searchParams, t]);

    if (!reservationId) return null;

    if (!ready) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    if (paymentFailed) {
        return <PaymentFailureView reservationId={reservationId} t={t} />;
    }

    return (
        <CoverageAddedContent
            reservationId={reservationId}
            insuranceName={info.insuranceName}
            amount={info.amount}
            cardLast4={info.cardLast4}
            t={t}
        />
    );
};

export default CoverageAddedPage;
